import { axiosDefaultInstance } from '@/http/default'
import type Place from '@/model/Place'
import type { PlaceBulkCreateRs, PlaceRequest } from '@/model/Place'
import { PlaceType } from '@/model/Place'

import type Page from '@/model/Page'
import { useProfileStore } from '@/store/profile.module'
import type { AxiosInstance } from 'axios'
import handleError from './util/ErrorHandler'
import type { ServiceError } from './util/ServiceError'

class PlaceService {
  private axiosInstance: AxiosInstance
  private storeProfile: any

  constructor() {
    this.axiosInstance = axiosDefaultInstance
    // this.storeProfile = useProfileStore()
  }

  async findAll(types?: PlaceType[]): Promise<Place[] | ServiceError> {
    this.storeProfile = useProfileStore()

    const params: string[][] = []
    if (types?.length) {
      types.forEach((type) => params.push(['types', type]))
    }
    const searchParams = new URLSearchParams(params)

    const companyId: string = this.storeProfile.selectedCompany.id

    try {
      const networkResponse = await this.axiosInstance.get<Place[]>(
        `${import.meta.env.VITE_API_BASE_URL}/company/${companyId}/place`,
        { params: searchParams }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async findById(id: string): Promise<Place | ServiceError> {
    this.storeProfile = useProfileStore()

    const companyId: string = this.storeProfile.selectedCompany.id

    try {
      const networkResponse = await this.axiosInstance.get<Place>(
        `${import.meta.env.VITE_API_BASE_URL}/company/${companyId}/place/${id}`
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async findAllV2(
    pageNumber?: number,
    pageSize?: number,
    name?: string,
    sort?: string,
    direction?: string,
    types?: PlaceType[]
  ): Promise<Page<Place> | ServiceError> {
    this.storeProfile = useProfileStore()

    const params: string[][] = []
    if (pageSize !== undefined) {
      params.push(['pageSize', String(pageSize)])
    }
    if (pageNumber !== undefined) {
      params.push(['pageNumber', String(pageNumber)])
    }
    if (name) {
      params.push(['name', name])
    }
    if (sort) {
      params.push(['sort', sort])
    }
    if (direction) {
      params.push(['direction', direction])
    }
    if (types?.length) {
      types.forEach((type) => params.push(['types', type]))
    }
    const searchParams = new URLSearchParams(params)

    const companyId: string = this.storeProfile.selectedCompany.id

    try {
      const networkResponse = await this.axiosInstance.get<Page<Place>>(
        `${import.meta.env.VITE_API_BASE_URL}/company/${companyId}/place/v2`,
        { params: searchParams }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async downloadAllCsv(name?: string, sort?: string, direction?: string): Promise<Blob | ServiceError> {
    this.storeProfile = useProfileStore()
    const locale = localStorage.getItem('appLanguage') || 'es'

    const params: string[][] = []
    if (name) {
      params.push(['name', name])
    }
    if (sort) {
      params.push(['sort', sort])
    }
    if (direction) {
      params.push(['direction', direction])
    }
    if (locale) {
      params.push(['locale', locale])
    }

    const searchParams = new URLSearchParams(params)
    const companyId: string = this.storeProfile.selectedCompany.id

    try {
      const networkResponse = await this.axiosInstance.get<Blob>(
        `${import.meta.env.VITE_API_BASE_URL}/company/${companyId}/place/csv`,
        { params: searchParams, responseType: 'blob' }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async createPlace(place: PlaceRequest): Promise<Place | ServiceError> {
    this.storeProfile = useProfileStore()

    const companyId: string = this.storeProfile.selectedCompany.id

    try {
      const networkResponse = await this.axiosInstance.post<Place>(
        `${import.meta.env.VITE_API_BASE_URL}/company/${companyId}/place`,
        place
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async updatePlace(place: PlaceRequest): Promise<Place | ServiceError> {
    this.storeProfile = useProfileStore()

    const companyId: string = this.storeProfile.selectedCompany.id

    try {
      const networkResponse = await this.axiosInstance.put<Place>(
        `${import.meta.env.VITE_API_BASE_URL}/company/${companyId}/place/${place.id}`,
        place
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async importCsv(
    file: File,
    columnMapping: { columnIndex: number; field: string }[],
    delimiter: string,
    defaultTypes: PlaceType[] = []
  ): Promise<PlaceBulkCreateRs | ServiceError> {
    this.storeProfile = useProfileStore()

    const companyId: string = this.storeProfile.selectedCompany.id

    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('columnMapping', JSON.stringify({
        columns: columnMapping,
        delimiter,
        ...(defaultTypes.length > 0 ? { defaultTypes } : {}),
      }))

      const networkResponse = await this.axiosInstance.post<PlaceBulkCreateRs>(
        `${import.meta.env.VITE_API_BASE_URL}/company/${companyId}/place/imports/csv`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async deletePlace(id: string): Promise<Place | ServiceError> {
    this.storeProfile = useProfileStore()

    const companyId: string = this.storeProfile.selectedCompany.id

    try {
      const networkResponse = await this.axiosInstance.delete<Place>(
        `${import.meta.env.VITE_API_BASE_URL}/company/${companyId}/place/${id}`
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }
}

export const placeService = new PlaceService()
